import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Textarea } from "@/core/components/ui/textarea";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { ApiClientDto } from "@/features/admin/api/dto";
import { useUpdateApiClientScopes } from "@/features/admin/api/use-update-api-client-scopes";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import {
  createApiClientScopesSchema,
  parseScopesInput,
  type ApiClientScopesFormValues,
} from "@/features/admin/schemas/api-client.schema";

/**
 * Remplace les scopes d'un client API (`PUT /ApiClients/{id}/scopes`,
 * §6.1). Saisie texte éclatée en tableau au submit.
 */
export function ApiClientScopesDialog({
  apiClient,
  open,
  onOpenChange,
}: {
  apiClient: ApiClientDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useAdminTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createApiClientScopesSchema(), [i18n.language]);

  const updateScopes = useUpdateApiClientScopes();
  const pending = updateScopes.isPending;

  const form = useForm({
    defaultValues: {
      scopes: apiClient.scopes.join(" "),
    } as ApiClientScopesFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      updateScopes.mutate(
        { apiClientId: apiClient.id, scopes: parseScopesInput(parsed.data.scopes) },
        {
          onSuccess: () => {
            notifSilence(t("admin.apiClients.feedback.scopesUpdated"));
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    },
  });

  // Réinitialise la saisie au changement de client.
  useEffect(() => {
    form.reset();
    form.setFieldValue("scopes", apiClient.scopes.join(" "));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé au changement de client uniquement.
  }, [apiClient.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("admin.apiClients.scopes.title")} —{" "}
            <NoTranslate as="span">{apiClient.name}</NoTranslate>
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="scopes"
            validators={{
              onChange: ({ value }) =>
                schema.shape.scopes.safeParse(value ?? "").success
                  ? undefined
                  : schema.shape.scopes.safeParse(value ?? "").error?.issues[0]
                      ?.message,
            }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="client-scopes">
                  {t("admin.apiClients.form.scopes")}
                </Label>
                <Textarea
                  id="client-scopes"
                  className="font-mono notranslate"
                  placeholder={t("admin.apiClients.form.scopesPlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
